import { useCallback, useState } from "react";
import { toast } from "react-toastify";

import { oids } from "@/configs/Settings";
import { useSessionContext } from "@/contexts/SessionContext";
import { useAuth } from "@/utils/useAuth";

import {
  buildIpesEmbeddedPortalUrl,
  sanitizeIpesIdentifier,
} from "./buildIpesEmbeddedPortalUrl";

/** Recorte mínimo do Patient FHIR — evita `any` e não acopla no tipo r5 completo. */
export interface IpesPatientLike {
  id?: string;
  identifier?: Array<{ system?: string; value?: string }>;
}

export interface UseIpesEmbeddedPortalInput {
  /** CPF já resolvido (prontuário: `state.patientId` / username em myrecords). */
  patientCpf?: string;
  /** Patient do atendimento — identifier CPF tem prioridade. */
  patient?: IpesPatientLike;
  /** Fallbacks (subject da encounter, patientId da rota). */
  patientCpfFallbacks?: Array<string | undefined>;
}

function firstCpfCandidate(
  values: Array<string | undefined | null>,
): string {
  for (const value of values) {
    const digits = sanitizeIpesIdentifier(value);
    if (digits) return digits;
  }
  return "";
}

/**
 * Extrai o CPF do Patient FHIR: identifier com `oids.cpf`, depois `id`.
 * Mesma ordem de `WearableDataView`.
 */
function extractPatientCpfFromResource(
  patient?: IpesPatientLike,
): string | undefined {
  const fromIdentifier = patient?.identifier?.find((item) =>
    item.system?.includes(oids.cpf),
  )?.value;
  return fromIdentifier || patient?.id;
}

const MISSING_PARAMS_TOAST =
  "Não foi possível abrir as informações de outras redes. Confirme o paciente, seu acesso e a organização selecionada.";

/**
 * Resolve params na hora do clique (token e org podem ter mudado)
 * e só então abre o Dialog com a URL pronta.
 */
export function useIpesEmbeddedPortal(input: UseIpesEmbeddedPortalInput) {
  const { user, access } = useSessionContext();
  const [isOpen, setIsOpen] = useState(false);
  const [iframeUrl, setIframeUrl] = useState("");

  const isProfessional = access?.type === "professional";

  const resolvePatientCpf = useCallback((): string => {
    return firstCpfCandidate([
      extractPatientCpfFromResource(input.patient),
      input.patientCpf,
      ...(input.patientCpfFallbacks ?? []),
    ]);
  }, [input.patient, input.patientCpf, input.patientCpfFallbacks]);

  const close = useCallback(() => {
    setIsOpen(false);
    // Limpa a URL para o próximo open remontar o iframe com token atual.
    setIframeUrl("");
  }, []);

  const open = useCallback(async () => {
    const resourceId = resolvePatientCpf();
    const subjectId = sanitizeIpesIdentifier(
      (await useAuth.getSubjectId()) || user?.username,
    );
    const organizationId = sanitizeIpesIdentifier(
      (await useAuth.getOrganizationId()) || access?.organizationId,
    );
    const token = (await useAuth.getXUserToken())?.trim() ?? "";

    const url = buildIpesEmbeddedPortalUrl({
      resourceId,
      subjectId,
      organizationId,
      token,
    });

    if (!url) {
      toast.warn(MISSING_PARAMS_TOAST);
      return;
    }

    setIframeUrl(url);
    setIsOpen(true);
  }, [access?.organizationId, resolvePatientCpf, user?.username]);

  return {
    isOpen,
    iframeUrl,
    isProfessional,
    open,
    close,
  };
}
